import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MadCloudResult } from '@common/http/models/mad-cloud-result.model';
import { ReviewFileUserStatus } from '@common/reviews/enums/review-file-user-status.enum';
import { ReviewPackageStatus } from '@common/reviews/enums/review-package-status.enum';
import { firstOrNull, makeArray } from '@common/util/array';
import { ErrorService } from '@portal-core/errors/services/error.service';
import { LicenseUserSeatType } from '@portal-core/license-users/enums/license-user-seat-type.enum';
import { LicenseUsersService } from '@portal-core/license-users/services/license-users.service';
import { TocFileFilter } from '@portal-core/project-files/constants/file-filters.constants';
import { ReviewPackage } from '@portal-core/reviews/review-packages/models/review-package.model';
import { ReviewPackagesService } from '@portal-core/reviews/review-packages/services/review-packages.service';
import { SnackBarAutoDismissDurationMS } from '@portal-core/ui/snack-bar/constants/snack-bar-auto-dismiss-duration.constant';
import { LoadingState } from '@portal-core/util/loading-state';
import { EMPTY, Observable } from 'rxjs';

interface ReviewPackageSettingsForm {
  description: FormControl<string>;
  name: FormControl<string>;
  ownerLicenseUserId: FormControl<number[]>;
  resetReviewerStatus: FormControl<boolean>;
  status: FormControl<ReviewPackageStatus>;
  tocPath: FormControl<string[]>;
}

@Component({
  selector: 'mc-review-package-settings-form',
  templateUrl: './review-package-settings-form.component.html',
  styleUrls: ['./review-package-settings-form.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ReviewPackageSettingsFormComponent implements OnChanges {
  @Input() licenseId: number;
  @Input() reviewPackage: ReviewPackage;
  @Output() cancel: EventEmitter<void> = new EventEmitter<void>();
  @Output() saved: EventEmitter<void> = new EventEmitter<void>();

  LicenseUserSeatType: typeof LicenseUserSeatType = LicenseUserSeatType;
  ReviewFileUserStatus: typeof ReviewFileUserStatus = ReviewFileUserStatus;
  ReviewPackageStatus: typeof ReviewPackageStatus = ReviewPackageStatus;
  TocFileFilter: string = TocFileFilter;

  savingState: LoadingState<string> = new LoadingState<string>();
  reviewSettingsForm: FormGroup<ReviewPackageSettingsForm>;

  get dirty(): boolean {
    return this.reviewSettingsForm.dirty;
  }

  get statusChangedToOpen(): boolean {
    if (this.reviewPackage) {
      return this.reviewPackage.Status !== ReviewPackageStatus.Open && this.reviewSettingsForm.value.status === ReviewPackageStatus.Open;
    } else {
      return false;
    }
  }

  get statusChangedToMerged(): boolean {
    if (this.reviewPackage) {
      return this.reviewPackage.Status !== ReviewPackageStatus.Merged && this.reviewSettingsForm.value.status === ReviewPackageStatus.Merged;
    } else {
      return false;
    }
  }

  constructor(
    private snackBar: MatSnackBar,
    private formBuilder: FormBuilder,
    private errorService: ErrorService,
    private reviewPackagesService: ReviewPackagesService,
    private licenseUsersService: LicenseUsersService
  ) {
    this.buildForm();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.reviewPackage) {
      this.resetForm(this.reviewPackage || ({} as ReviewPackage));
    }
  }

  onCancelClicked() {
    this.cancel.emit();
  }

  onSubmit() {
    if (!this.reviewSettingsForm.valid) {
      return;
    }

    let ownerUserId: string = null;
    const ownerLicenseUserIds = this.reviewSettingsForm.get('ownerLicenseUserId').value;
    if (typeof ownerLicenseUserIds?.[0] === 'number') {
      ownerUserId = this.licenseUsersService.getItemById(ownerLicenseUserIds[0])?.User?.Id;
    }

    this.savingState.update(true);

    let mergeSucceeded = false;

    // If the review package status is changed to merged, merge the review package files to the workspace
    (this.statusChangedToMerged ? this.reviewPackagesService.mergeReviewPackageToWorkspace$(ownerUserId, this.reviewPackage.Id) : EMPTY).subscribe({
      next: result => {
        const failedFiles = result?.Result?.fileResults.filter(file => file.status === 'failed');

        if (failedFiles?.length) {
          this.savingState.update(false, 'Unable to update your workspace for the following review files', failedFiles.map(file => file.path));
        } else if (result?.Errors) {
          this.savingState.update(false, 'Unable to update your workspace.', this.errorService.getErrorMessages(result.Errors));
        } else if (result?.Succeeded) {
          mergeSucceeded = true;
        }
      },
      complete: () => {
        // If the merge to the workspace failed, don't proceed with saving the package settings and preserve the merge failure state that was set in `next`
        if (this.statusChangedToMerged && !mergeSucceeded) {
          return;
        }

        // Save the package settings after walking the user through the merge workflow
        let settingsSucceeded = true;

        this.updatePackageSettings$(ownerUserId).subscribe({
          next: result => {
            if (result?.Errors) {
              settingsSucceeded = false;
              this.savingState.update(false, 'Unable to update your review package settings.', this.errorService.getErrorMessages(result.Errors));
            }
          },
          complete: () => {
            // Preserve the failure state set in `next` instead of clearing it
            if (!settingsSucceeded) {
              return;
            }

            // Don't interfere with the review merge success snackbar
            if (!this.statusChangedToMerged) {
              this.snackBar.open('Your settings have been saved.', 'OK', { duration: SnackBarAutoDismissDurationMS });
            }
            this.savingState.update(false);
            this.reviewSettingsForm.markAsPristine();
            this.saved.emit();
          },
          error: (err) => this.savingState.update(false, 'Unable to update your review package settings.', this.errorService.getErrorMessages(err))
        });
      },
      error: error => {
        this.savingState.update(false, 'Unable to update your workspace.', this.errorService.getErrorMessages(error));
      }
    });
  }

  private updatePackageSettings$(authorUserId: string): Observable<MadCloudResult> {
    // When only the status is changed to merged, there is no need to set the status because the collab authority does so on success. There is nothing to do.
    const isOnlyMerge = authorUserId === this.reviewPackage.AuthorUserId &&
      this.reviewSettingsForm.value.description === this.reviewPackage.Description &&
      this.reviewSettingsForm.value.name === this.reviewPackage.Name &&
      firstOrNull(this.reviewSettingsForm.value.tocPath ?? []) === this.reviewPackage.TocPath &&
      this.statusChangedToMerged;

    if (isOnlyMerge) {
      return EMPTY;
    }

    return this.reviewPackagesService.updateReviewPackage$({
      AuthorUserId: authorUserId,
      Description: this.reviewSettingsForm.value.description,
      Id: this.reviewPackage.Id,
      Name: this.reviewSettingsForm.value.name,
      Status: this.reviewSettingsForm.value.status,
      TocPath: firstOrNull(this.reviewSettingsForm.value.tocPath)
    }, this.statusChangedToOpen && this.reviewSettingsForm.value.resetReviewerStatus);
  }

  private buildForm() {
    this.reviewSettingsForm = this.formBuilder.group<ReviewPackageSettingsForm>({
      description: new FormControl(''),
      name: new FormControl('', [Validators.required]),
      ownerLicenseUserId: new FormControl<number[]>(null),
      resetReviewerStatus: new FormControl<boolean>(false, { nonNullable: true }),
      status: new FormControl<ReviewPackageStatus>(null, [Validators.required]),
      tocPath: new FormControl<string[]>(null)
    });
  }

  private resetForm(reviewPackage: ReviewPackage) {
    const ownerLicenseUserId =
      reviewPackage.AuthorUserId && reviewPackage.LicenseId
        ? this.licenseUsersService.getLicenseUserByUserId(reviewPackage.AuthorUserId, reviewPackage.LicenseId)?.Id
        : null;

    this.reviewSettingsForm.reset({
      description: reviewPackage.Description,
      name: reviewPackage.Name,
      ownerLicenseUserId: typeof ownerLicenseUserId === 'number' ? [ownerLicenseUserId] : null,
      status: reviewPackage.Status,
      tocPath: makeArray(reviewPackage.TocPath)
    });
  }
}
